/**
 * Current Interview Section Component
 * Displays scorecards for the current interview
 */

import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Flex,
} from '@chakra-ui/react';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  type GroupedScorecards,
  type ScorecardItem,
  type ScorecardVisibilityMode,
} from '../types';
import { useCurrentInterviewTableConfig } from './table-config';
import { type InterviewType } from '@/types/interview';

interface CurrentInterviewSectionProps {
  scorecards: ScorecardItem[];
  interview: InterviewType;
  currentUserId: number;
  tabId: string;
  hasHistoryData: boolean;
  mode: ScorecardVisibilityMode;
  hasCurrentUserSubmitted: boolean;
  onSubmitScorecard: (_: ScorecardItem) => void;
  onViewScorecard: (_: ScorecardItem) => void;
  onEditScorecard: (_: ScorecardItem) => void;
  onSendReminder: (_: ScorecardItem) => void;
  refetch: () => void;
  transferInfo?: GroupedScorecards['transferInfo'];
}

export const CurrentInterviewSection = ({
  scorecards,
  interview,
  currentUserId,
  hasHistoryData,
  mode,
  tabId,
  hasCurrentUserSubmitted,
  onSubmitScorecard,
  onViewScorecard,
  onEditScorecard,
  onSendReminder,
  refetch,
  transferInfo,
}: CurrentInterviewSectionProps) => {
  const { feScorecardVisibility } = useKFlags();
  const tableConfig = useCurrentInterviewTableConfig({
    scorecards,
    interview,
    currentUserId,
    mode,
    hasCurrentUserSubmitted,
    feScorecardVisibility,
    onSubmitScorecard,
    onViewScorecard,
    onEditScorecard,
    onSendReminder,
    refetch,
    transferInfo,
  });

  if (scorecards?.length === 0) {
    return null;
    // <EmptyResults
    //   icon={CsvFailed}
    //   wrapperProps={{ h: '100%' }}
    //   text="No scorecards available"
    //   iconProps={{ h: '140px', w: '142px', mb: '12px' }}
    // />
  }

  return hasHistoryData && tabId === 'all_scorecards' ? (
    <AccordionItem border="none">
      <AccordionButton
        w="100%"
        _focus={{ boxShadow: 'none' }}
        borderBottom="1px solid"
        borderColor="border-color"
        justifyContent="space-between"
        p="0"
        h="54px"
        bg="secondary-25"
        _hover={{}}
      >
        <Center
          w="54px"
          h="100%"
          borderRight="1px solid"
          borderColor="border-color"
        >
          <AccordionIcon />
        </Center>
        <Flex flex="1" alignItems="center">
          <Box px="12px">
            <KText size="md-medium" fontWeight="550" color="secondary-900">
              This interview
            </KText>
          </Box>
          <Box
            border="1px solid"
            borderColor="border-color"
            bg="base-white"
            borderRadius="6px"
            minWidth="24px"
            minHeight="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p="0px 6px"
          >
            <KText size="sm-medium">{scorecards?.length || 0}</KText>
          </Box>
        </Flex>
      </AccordionButton>
      <AccordionPanel p="0" overflow="hidden">
        <Table
          columns={tableConfig}
          data={scorecards}
          tableId="current_interview_scorecards"
          skipPagination={true}
          layoutStyles={{ px: '12px', py: '12px' }}
          layoutScrollContainerStyles={{
            style: { overflowX: 'auto', overflowY: 'visible' },
          }}
        />
      </AccordionPanel>
    </AccordionItem>
  ) : (
    <Table
      columns={tableConfig}
      data={scorecards}
      tableId="current_interview_scorecards"
      skipPagination={true}
      layoutStyles={{ px: '8px', py: '8px' }}
      layoutScrollContainerStyles={{
        style: { overflowX: 'auto', overflowY: 'visible' },
      }}
    />
  );
};
